import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Save, Loader2, Info, Image as ImageIcon, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

const CompanySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    trade_name: '',
    nif: '',
    address: '',
    postal_code: '',
    city: '',
    country: 'Portugal',
    phone: '',
    email: '',
    website: '',
    logo_url: '',
    logo_path: ''
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/api/po-documents/settings/company-profile`);
      setProfile(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      toast.error('Erro ao carregar perfil da empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/po-documents/settings/company-profile`, profile);
      toast.success('Perfil da empresa atualizado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[900px] mx-auto px-4 md:px-6 py-4 md:py-6">
          <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
            Perfil da Empresa
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Dados institucionais utilizados em documentos PDF e emails
          </p>
        </div>
      </div>

      <div className="max-w-[900px] mx-auto px-4 md:px-6 py-4 md:py-6">
        <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-red-600" />
            <CardTitle className="text-lg">Dados da Empresa</CardTitle>
          </div>
          <CardDescription>
            Estes dados aparecem nos PDFs de encomendas enviados a fornecedores
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid gap-6">
            {/* Logo Section */}
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-48 h-16 bg-white rounded border border-slate-200 flex items-center justify-center overflow-hidden">
                  {profile.logo_path ? (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="text-sm font-medium">Logo OK</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-slate-400">
                      <ImageIcon className="h-5 w-5" />
                      <span className="text-sm">Sem logo</span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-slate-900 flex items-center gap-2">
                    <ImageIcon className="h-4 w-4 text-red-600" />
                    Logótipo
                  </h4>
                  {profile.logo_path ? (
                    <>
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        O logótipo da Chaviarte está configurado e será incluído automaticamente nos PDFs de encomendas.
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Localização: {profile.logo_path}
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-slate-500 mt-1">
                      Nenhum logótipo configurado. Entre em contacto com o suporte para adicionar o logótipo da empresa.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Identification */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Denominação Social *</Label>
                <Input
                  id="name"
                  value={profile.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Nome completo da empresa"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="trade_name">Nome Comercial</Label>
                <Input
                  id="trade_name"
                  value={profile.trade_name}
                  onChange={(e) => handleChange('trade_name', e.target.value)}
                  placeholder="Ex: Chaviarte"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nif">NIF *</Label>
                <Input
                  id="nif"
                  value={profile.nif}
                  onChange={(e) => handleChange('nif', e.target.value)}
                  placeholder="Número de identificação fiscal"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">País</Label>
                <Input
                  id="country"
                  value={profile.country}
                  onChange={(e) => handleChange('country', e.target.value)}
                />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="address">Morada</Label>
              <Input
                id="address"
                value={profile.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="Rua, número, etc."
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="postal_code">Código Postal</Label>
                <Input
                  id="postal_code"
                  value={profile.postal_code}
                  onChange={(e) => handleChange('postal_code', e.target.value)}
                  placeholder="Ex: 4410-022"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Localidade</Label>
                <Input
                  id="city"
                  value={profile.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="Ex: Vila Nova de Gaia"
                />
              </div>
            </div>

            {/* Contact */}
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Telefone</Label>
                <Input
                  id="phone"
                  value={profile.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="Ex: 227 123 456"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={profile.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="geral@empresa.pt"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={profile.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="www.empresa.pt"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-8 pt-6 border-t border-slate-100">
            <Button onClick={handleSave} disabled={saving} className="bg-[#C41E3A] hover:bg-[#a01830]">
              {saving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A guardar...</>
              ) : (
                <><Save className="h-4 w-4 mr-2" />Guardar Alterações</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default CompanySettings;
